<template>
  <b-container fluid>
    <!-- for Desktop Only Progress -->
    <OnboardingProgress
      :hide="hideBackButton"
      :progress="progress"
      @back="prev"
    />

    <OnboardingWrapper>
      <template #header>
        <OnboardingHeader
          v-if="!isOnboardingRedesign"
          step="4"
          :hide="hideBackButton"
          :hide-step="isNonDocInfoSlide"
          :title="title"
          @back="prev"
        />
      </template>
      <template #right>
        <div
          v-if="!isOnboardingRedesign"
          class="mt-6 tw-w-full tw-flex tw-justify-center"
        >
          <OnboardingCertifiedSeal
            v-if="[
              'information',
              'idphoto',
              'personalphoto',
            ].includes(step)"
          />
        </div>
      </template>
      <MobileOnboardingProgress
        label="Step 3 of 3"
        start-step="Onboarding Completion"
        end-step=""
      />
      <b-progress
        :value="mobileProgressDisplay"
        :max="100"
        class="step-progress mb-5"
        :class="isOnboardingRedesign ? 'tw-block md:tw-hidden' : 'd-lg-none'"
        variant="purple"
      />
      <b-alert
        :show="!isOnboardingRedesign && alert.show"
        :variant="alert.type"
      >
        {{
          alert.message
        }}
      </b-alert>
      <VerificationInfo
        v-if="step == 'information' && userData.onboardingversion == 'V3'"
        :analytics-id="analyticsId"
        :state="state"
        :phoneno="userData.phoneno"
        :email="userData.email"
        :firstname="userData.firstname"
        :lastname="userData.lastname"
        :onboardingflow="onboardingflow"
        @setStep="setStep"
      />
      <PatientInformation
        v-if="step == 'information' && userData.onboardingversion != 'V3'"
        :nonrx="userData.nonrxmedicationonly"
        :onboarding-data="userData"
        :analytics-id="analyticsId"
        :onboardingflow="onboardingflow"
        @setStep="setStep"
        @showAlert="showAlert"
        @updateUserInfo="updateUserInfo"
      />
      <IDPhoto
        v-if="step === 'idphoto' || step === 'idphotoskip'"
        ref="idphoto"
        :class="{'tw-hidden': step === 'idphotoskip'}"
        :onboardingflow="onboardingflow"
        :state="state"
        :analytics-id="analyticsId"
        :step="step"
        @setStep="setStep"
        @showAlert="showAlert"
        @hideAlert="hideAlert"
      />
      <template v-if="isOnboardingRedesign">
        <Nothing
          v-if="step === 'idphotoskip'"
          :body="{
            id: 'idphotoskip',
            title: 'You can bypass this step now, but you will need to upload your ID in order for us to verify your identity before your medical provider can prescribe medication.',
          }"
          :is-skipping="isSkipping"
          @submitAnswer="setStep('idphoto')"
          @skip="onSkip('idphoto')"
        />
      </template>
      <PersonalPhoto
        v-if="step === 'personalphoto' || step === 'personalphotoskip'"
        ref="personalphoto"
        :class="{'tw-hidden': step === 'personalphotoskip'}"
        :onboardingflow="onboardingflow"
        :flow-variant="userData.flow_variant"
        :state="state"
        :analytics-id="analyticsId"
        :step="step"
        @showAlert="showAlert"
        @hideAlert="hideAlert"
        @setStep="setStep"
      />
      <template v-if="isOnboardingRedesign">
        <Nothing
          v-if="step === 'personalphotoskip'"
          :body="{
            id: 'personalphotoskip',
            title: 'You can bypass this step now, but you will need to upload a photo of yourself  in order for us to verify your identity before your medical provider can prescribe medication.',
          }"
          :is-skipping="isSkipping"
          @submitAnswer="setStep('personalphoto')"
          @skip="onSkip('personalphoto')"
        />
      </template>
      <Complete
        v-if="step == 'complete'"
        :body="userData"
        @exit="exiting = true"
      />

      <!-- Non-Document Verification Slides -->

      <NonDocInfo
        v-if="step == 'nondocinfo'"
        :analytics-id="analyticsId"
        :firstname="userData.firstname"
        :lastname="userData.lastname"
        :onboardingflow="onboardingflow"
        @next="setStep('nondocssn')"
      />

      <NonDocForm
        v-if="step == 'nondocssn'"
        :state="state"
        :analytics-id="analyticsId"
        :firstname="userData.firstname"
        :lastname="userData.lastname"
        :onboardingflow="onboardingflow"
        @next="(step) => setStep(step)"
        @lock-ssn="lockSSN"
        @clear-verif-data="clearVerificationData"
      />
      <div
        v-if="0"
        class="d-block text-center mt-4 mb-4"
      >
        <div class="d-none d-lg-block mt-4"></div>
        <small><b-icon icon="lock-fill" /> 256-BIT TLS SECURITY</small>
      </div>
      <ErrorLabel
        v-if="isOnboardingRedesign && alert.show"
        class="tw-mt-3"
      >
        {{ alert.message }}
      </ErrorLabel>
    </OnboardingWrapper>
  </b-container>
</template>

<script>
import { mapState } from 'vuex';
// components
import VerificationInfo from '@/components/onboarding/verification/VerificationInfo';
import PatientInformation from '@/components/onboarding/verification/PatientInformation';
import IDPhoto from '@/components/onboarding/verification/IDPhoto';
import PersonalPhoto from '@/components/onboarding/verification/PersonalPhoto';
import OnboardingHeader from '@/components/partials/OnboardingHeader';
import OnboardingWrapper from '@/components/partials/OnboardingWrapper';
import Complete from '@/components/onboarding/checkout/Complete';
import OnboardingProgress from '@/components/partials/OnboardingProgress';
import OnboardingCertifiedSeal from '@/components/onboarding/OnboardingCertifiedSeal';
import NonDocInfo from '@/components/onboarding/verification/nondoc/NonDocInfo';
import NonDocForm from '@/components/onboarding/verification/nondoc/NonDocForm';
import Nothing from '@/components/onboarding/questions/Nothing';

// mixins
import { onboardingCtaFooter } from '@/mixins/onboardingCtaFooter';
import analyticsMixin from '@/mixins/analytics';
import ErrorLabel from '@/components/partials/ErrorLabel.vue';
import MobileOnboardingProgress from '@/components/partials/MobileOnboardingProgress.vue';

export default {
  components: {
    MobileOnboardingProgress,
    ErrorLabel,
    VerificationInfo,
    PatientInformation,
    IDPhoto,
    PersonalPhoto,
    OnboardingHeader,
    Complete,
    OnboardingProgress,
    OnboardingCertifiedSeal,
    NonDocInfo,
    NonDocForm,
    Nothing,
    OnboardingWrapper
  },
  mixins: [onboardingCtaFooter, analyticsMixin],
  beforeRouteLeave(to, from, next) {
    // Check if there's a modal open
    if (this.$store.state.global.isModalOpen) {
      next(false);
      return;
    }

    if (
      (this.step == 'information' || to.path == '/checkout' || this.step == 'nondocform' || (this.isOnboardingRedesign && this.step == 'nondocinfo') || (this.userData.hasOwnProperty('has_completed_non_doc_verification') && this.step == 'idphoto')) &&
      this.onboardingflow != 'flowB' 
    ) {
      next();
    } else if (
      this.onboardingflow == 'flowB' &&
      !this.exiting &&
      (this.step == 'information')
    ) {
      next(false);
    } else if (this.exiting) {
      next();
    } else {
      this.prev();
      next(false);
    }
  },
  props: {
    title: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      alert: {
        show: false,
        message: '',
      },
      step: '',
      analyticsId: null,
      state: '',
      userData: {},
      exiting: false,
      onboardingflow: 'flowA',

      // for new progress UI update on the header
      progress: [
        {
          title: 'Account Creation',
          value: 100,
          max: 100,
        },
        {
          title: 'Intake',
          value: 5,
          max: 5,
        },
        {
          title: 'Onboarding Completion',
          value: 2,
          max: 5,
        },
      ],
      mobileProgressDisplay: 70,
      isSkipping: false
    };
  },
  computed: {
    ...mapState('configuration', ['config']),
    ...mapState('global', ['isOnboardingRedesign']),
    hideBackButton() {
      if (this.isOnboardingRedesign && this.step === 'nondocinfo') {
        return false;
      }
      if (
        (this.step !== 'information' || this.step !== 'complete') &&
        this.onboardingflow != 'flowB' && (!this.isNonDocInfoSlide)
      ) {
        return false;
      }
      return true;
    },
    isNonDocFaceState() {
      return this.config.non_flow_face.includes(this.state);
    },
    showNonDocForm() {
      return (['B', 'C', 'D1', 'D2', 'E1', 'F1', 'F2', 'F3v1', 'F3v2', 'G', 'H', 'J', '1225-onboarding-core-v1', 'K'].includes(this.userData.flow_variant)) &&
        this.step == 'nondocform';
    },
    displayNonDoc() {
      return (['B', 'C', 'D1', 'D2', 'E1', 'F1', 'F2', 'F3v1', 'F3v2', 'G', 'H', 'J', '1225-onboarding-core-v1', 'K'].includes(this.userData.flow_variant)) &&
        (this.userData.has_completed_non_doc_verification == null 
        || this.userData.has_completed_non_doc_verification == undefined);
    }
  },
  watch: {
    step: {
      immediate: true,
      handler (step) {
        switch (step) {
          case 'information':
          case 'nondocinfo':
            this.mobileProgressDisplay = this.onboardingflow === 'flowA' ? 70 : 85;
            this.onboardingflow === 'flowA'
              ? this.$set(this.progress[2], 'value', 2)
              : this.$set(this.progress[2], 'value', 3);
            break;
          case 'idphoto':
            this.mobileProgressDisplay = this.onboardingflow === 'flowA' ? 75 : 90;
            this.onboardingflow === 'flowA'
              ? this.$set(this.progress[2], 'value', 2.4)
              : this.$set(this.progress[2], 'value', 3.5);
            break;
          case 'personalphoto':
            this.mobileProgressDisplay = this.onboardingflow === 'flowA' ? 80 : 95;
            this.onboardingflow === 'flowA'
              ? this.$set(this.progress[2], 'value', 2.8)
              : this.$set(this.progress[2], 'value', 4);
            break;
          case 'complete':
            this.mobileProgressDisplay = 100;
            this.$set(this.progress[2], 'value', 5);
            break;
        }
      }
    },
  },
  async mounted() {
    await this.loadOnboarding();
    this.loadAnalytics();
  },
  methods: {
    async lockSSN() {
      this.userData.has_completed_non_doc_verification = false;
      await this.$nextTick()
      this.setStep('idphoto')
    },
    clearVerificationData() {
      if (this.userData.identificationURL) {
        this.userData.identificationURL = '';
      }
      if (this.userData.photoURL) {
        this.userData.photoURL = '';
      }
    },
    updateUserInfo(userinfo) {
      this.userData.firstname = userinfo.firstname;
      this.userData.lastname = userinfo.lastname;
      this.userData.phoneno = userinfo.phoneno.replace('+1', '');
    },
    showAlert(message) {
      this.alert = {
        show: true,
        type: 'warning',
        message: message,
      };
    },
    hideAlert() {
      this.alert.show = false;
    },
    setStep(step) {
      this.alert.show = false;
      this.step = step;
    },
    prev() {
      switch (this.step) {
        case 'information':
          if (this.onboardingflow == 'flowB') {
            break;
          } else {
            this.$router.push('/treatment-options').catch(() => {});
            break;
          }
        case 'nondocinfo':
          if (this.isOnboardingRedesign) {
            this.$router.push('/treatment-options')
          }
          break;
        case 'nondocssn' :
          this.step = 'nondocinfo';
          break;
        case 'idphoto':
          if (this.displayNonDoc && (
            this.userData.has_completed_non_doc_verification == null || 
            this.userData.has_completed_non_doc_verification == undefined)
          ) {
            this.step = 'nondocssn';
          } else {
            if (this.userData.flow_variant == 'B') {
              this.$router.push('/treatment-options').catch(() => {});
            } else {
              if (['H', 'J', '1225-onboarding-core-v1', 'K'].includes(this.userData.flow_variant)) {
                this.$router.push('/treatment-options').catch(() => {});
              } else {
                this.step = 'information';
              }
            }
          }
          break;
        case 'idphotoskip':
          this.step = 'idphoto';
          break;
        case 'personalphotoskip':
          this.step = 'personalphoto';
          break;
        case 'personalphoto':
          if (this.userData.flow_variant == 'B' && this.isNonDocFaceState && this.userData.has_completed_non_doc_verification) {
            this.exiting = true;
            this.$router.push('/treatment-options').catch(() => {});
          } else {
            this.step = 'idphoto';
          }
          break;
      }
    },
    async onSkip(step) {
      this.isSkipping = true;
      console.log(this.$refs.idphoto)
      if (step === 'idphoto') {
        await this.$refs.idphoto.submitAnswer({ isSkipped: true })
      } else {
        await this.$refs.personalphoto.next({ isSkipped: true })
      }
      this.isSkipping = false;
    },
    async loadOnboarding() {
      try {
        const data = await this.$store.dispatch('onboarding/getPatientOnboarding');
        this.userData = data;
        this.analyticsId = data.usercustomid ? data.usercustomid : data.id;
        this.onboardingflow = data.onboardingflow;
        this.state = data.state;
        if (data.onboardingstep == 5.2) {
          if (this.displayNonDoc) {
            this.step = 'nondocinfo';
            this.userData.has_completed_non_doc_verification = null;
          } else {
            // Variant B, but no longer eligible for non-doc
            if (this.userData.flow_variant == 'B') {
              this.step = 'idphoto';
            } else {
              this.step = 'information';
            }
          }
        } else if (data.onboardingstep >= 5.3) {
          this.step = 'personalphoto';
        } else if (data.onboardingstep < 5) {
          window.location.href = '/';
        } else {
          this.step = 'information';
        }

        if (['H', 'J', '1225-onboarding-core-v1', 'K'].includes(this.userData.flow_variant) && this.step == 'information') {
          this.step = 'idphoto';
        }
      } catch (err) {
        this.exiting = true;
        const id = this.userid ? this.userid : 'Could not get customer ID';
        const email = this.userData.email
          ? this.userData.email
          : 'Could not get patient email';
        await this.$store.dispatch('sendErrorLog', {
          step: 'Identity Verification - Loading',
          cus_id: id,
          email: email,
          error_log: err,
        });
        this.$router.push('/login');
      }
    },
    loadAnalytics() {
      try {
        this.$_analytics_page();
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>
